import React, { useCallback, useRef, useEffect } from 'react';
import {
  Box, Typography, IconButton, Tooltip, Stack, Chip,
} from '@mui/material';
import {
  Undo as UndoIcon,
  Redo as RedoIcon,
  History as HistoryIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

// Action types for undo/redo
export const ACTION_TYPES = {
  ADD_NODE: 'ADD_NODE',
  DELETE_NODE: 'DELETE_NODE',
  UPDATE_NODE: 'UPDATE_NODE',
  MOVE_NODE: 'MOVE_NODE',
  ADD_EDGE: 'ADD_EDGE',
  DELETE_EDGE: 'DELETE_EDGE',
  UPDATE_EDGE: 'UPDATE_EDGE',
  UPDATE_GLOBAL_STATE: 'UPDATE_GLOBAL_STATE',
  BATCH_OPERATION: 'BATCH_OPERATION',
};

// History entry structure
class HistoryEntry {
  constructor(action, data, timestamp = Date.now()) {
    this.id = `action_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
    this.action = action;
    this.data = data;
    this.timestamp = timestamp;
    this.description = this.generateDescription();
  }

  generateDescription() {
    const actionDescriptions = {
      [ACTION_TYPES.ADD_NODE]: `Add ${this.data.node?.type || 'node'}`,
      [ACTION_TYPES.DELETE_NODE]: `Delete ${this.data.node?.type || 'node'}`,
      [ACTION_TYPES.UPDATE_NODE]: `Update ${this.data.node?.type || 'node'}`,
      [ACTION_TYPES.MOVE_NODE]: `Move ${this.data.node?.type || 'node'}`,
      [ACTION_TYPES.ADD_EDGE]: 'Add connection',
      [ACTION_TYPES.DELETE_EDGE]: 'Delete connection',
      [ACTION_TYPES.UPDATE_EDGE]: 'Update connection',
      [ACTION_TYPES.UPDATE_GLOBAL_STATE]: 'Update flow state',
      [ACTION_TYPES.BATCH_OPERATION]: `Batch: ${this.data.operations?.length || 0} operations`,
    };

    return actionDescriptions[this.action] || this.action;
  }
}

// Undo/Redo Manager Hook
export const useUndoRedo = (initialState = {}) => {
  const historyRef = useRef([]);
  const currentIndexRef = useRef(-1);
  const maxHistorySize = 50; // Limit history size for performance

  // Initialize with initial state
  useEffect(() => {
    if (historyRef.current.length === 0) {
      const initialStateEntry = new HistoryEntry(
        ACTION_TYPES.BATCH_OPERATION,
        {
          operations: [{ type: 'INITIAL_STATE', data: initialState }],
          snapshot: initialState,
        },
      );
      historyRef.current = [initialStateEntry];
      currentIndexRef.current = 0;
    }
  }, []);

  // Add action to history
  const addAction = useCallback((action, data) => {
    const entry = new HistoryEntry(action, data);

    // Remove any future history if we're not at the end
    if (currentIndexRef.current < historyRef.current.length - 1) {
      historyRef.current = historyRef.current.slice(0, currentIndexRef.current + 1);
    }

    // Add new entry
    historyRef.current.push(entry);
    currentIndexRef.current = historyRef.current.length - 1;

    // Limit history size
    if (historyRef.current.length > maxHistorySize) {
      historyRef.current.shift();
      currentIndexRef.current--;
    }

    return entry;
  }, []);

  // Undo last action
  const undo = useCallback(() => {
    if (currentIndexRef.current > 0) {
      currentIndexRef.current--;
      return historyRef.current[currentIndexRef.current];
    }
    return null;
  }, []);

  // Redo next action
  const redo = useCallback(() => {
    if (currentIndexRef.current < historyRef.current.length - 1) {
      currentIndexRef.current++;
      return historyRef.current[currentIndexRef.current];
    }
    return null;
  }, []);

  // Get current state
  const getCurrentState = useCallback(() => {
    if (currentIndexRef.current >= 0 && currentIndexRef.current < historyRef.current.length) {
      return historyRef.current[currentIndexRef.current];
    }
    return null;
  }, []);

  // Get history
  const getHistory = useCallback(() => ({
    entries: [...historyRef.current],
    currentIndex: currentIndexRef.current,
    canUndo: currentIndexRef.current > 0,
    canRedo: currentIndexRef.current < historyRef.current.length - 1,
  }), []);

  // Clear history
  const clearHistory = useCallback(() => {
    historyRef.current = [];
    currentIndexRef.current = -1;
  }, []);

  // Create snapshot of current state
  const createSnapshot = useCallback((state) => addAction(ACTION_TYPES.BATCH_OPERATION, {
    operations: [{ type: 'SNAPSHOT', data: state }],
    snapshot: state,
  }), [addAction]);

  return {
    addAction,
    undo,
    redo,
    getCurrentState,
    getHistory,
    clearHistory,
    createSnapshot,
  };
};

// Undo/Redo Controls Component
export function UndoRedoControls({
  undoManager,
  onUndo,
  onRedo,
  onClearHistory,
  showHistory = false,
  onToggleHistory = () => {},
}) {
  const history = undoManager?.getHistory() || { canUndo: false, canRedo: false };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Tooltip title={`Undo (Ctrl+Shift+Z)${history.canUndo ? '' : ' - No actions to undo'}`}>
        <span>
          <IconButton
            size="small"
            onClick={onUndo}
            disabled={!history.canUndo}
            color="primary"
          >
            <UndoIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={`Redo (Ctrl+Shift+Y)${history.canRedo ? '' : ' - No actions to redo'}`}>
        <span>
          <IconButton
            size="small"
            onClick={onRedo}
            disabled={!history.canRedo}
            color="primary"
          >
            <RedoIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      {showHistory && (
        <Tooltip title="Show History">
          <IconButton
            size="small"
            onClick={onToggleHistory}
            color="info"
          >
            <HistoryIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Clear History">
        <IconButton
          size="small"
          onClick={onClearHistory}
          color="error"
          disabled={history.entries?.length === 0}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* History indicator */}
      {history.entries && (
        <Chip
          label={`${history.currentIndex + 1}/${history.entries.length}`}
          size="small"
          variant="outlined"
          color="info"
        />
      )}
    </Stack>
  );
}

// History Panel Component
export function HistoryPanel({
  undoManager,
  open,
  onClose,
  onJumpToEntry = () => {},
}) {
  const history = undoManager?.getHistory() || { entries: [], currentIndex: -1 };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
      onClick={onClose}
    >
      <Box
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 2,
          p: 3,
          maxWidth: 600,
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: 24,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, mb: 3,
        }}
        >
          <HistoryIcon color="primary" />
          <Typography variant="h5" component="h2">
            Action History
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Click on any action to jump to that point in history
        </Typography>

        {history.entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No actions in history
          </Typography>
        ) : (
          <Stack spacing={1}>
            {history.entries.map((entry, index) => (
              <Box
                key={entry.id}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: 1,
                  borderColor: index === history.currentIndex ? 'primary.main' : 'divider',
                  backgroundColor: index === history.currentIndex ? 'primary.lighter' : 'background.default',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: index === history.currentIndex ? 'primary.light' : 'action.hover',
                  },
                }}
                onClick={() => onJumpToEntry(index)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" fontWeight={index === history.currentIndex ? 600 : 400}>
                      {entry.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(entry.timestamp).toLocaleTimeString()}
                    </Typography>
                  </Box>
                  {index === history.currentIndex && (
                    <Chip label="Current" size="small" color="primary" />
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

// Utility functions for common operations
export const UndoRedoUtils = {
  // Create add node action
  createAddNodeAction: (node) => ({
    type: ACTION_TYPES.ADD_NODE,
    data: { node: { ...node } },
  }),

  // Create delete node action
  createDeleteNodeAction: (node) => ({
    type: ACTION_TYPES.DELETE_NODE,
    data: { node: { ...node } },
  }),

  // Create update node action
  createUpdateNodeAction: (nodeId, oldData, newData) => ({
    type: ACTION_TYPES.UPDATE_NODE,
    data: { nodeId, oldData: { ...oldData }, newData: { ...newData } },
  }),

  // Create move node action
  createMoveNodeAction: (nodeId, oldPosition, newPosition) => ({
    type: ACTION_TYPES.MOVE_NODE,
    data: { nodeId, oldPosition: { ...oldPosition }, newPosition: { ...newPosition } },
  }),

  // Create add edge action
  createAddEdgeAction: (edge) => ({
    type: ACTION_TYPES.ADD_EDGE,
    data: { edge: { ...edge } },
  }),

  // Create delete edge action
  createDeleteEdgeAction: (edge) => ({
    type: ACTION_TYPES.DELETE_EDGE,
    data: { edge: { ...edge } },
  }),

  // Create batch operation action
  createBatchAction: (operations) => ({
    type: ACTION_TYPES.BATCH_OPERATION,
    data: { operations: operations.map((op) => ({ ...op })) },
  }),
};

export default {
  useUndoRedo,
  UndoRedoControls,
  HistoryPanel,
  UndoRedoUtils,
  ACTION_TYPES,
  HistoryEntry,
};
